import { Field as ChakraField, useFieldContext } from "@chakra-ui/react";
import type { ComponentProps, PropsWithChildren, ReactNode } from "react";
import OptionalIndicator from "@ui-kit/form/OptionalIndicator/OptionalIndicator.web";

type ChakraFieldRootProps = ComponentProps<typeof ChakraField.Root>;
type ChakraFieldLabelProps = ComponentProps<typeof ChakraField.Label>;
type ChakraFieldErrorTextProps = ComponentProps<typeof ChakraField.ErrorText>;
type ChakraFieldHelperTextProps = ComponentProps<typeof ChakraField.HelperText>;

export type FormControlProps = PropsWithChildren<
  Omit<
    ChakraFieldRootProps,
    "disabled" | "invalid" | "required" | "readOnly"
  > & {
    // v1/v2 props
    isDisabled?: boolean;
    isInvalid?: boolean;
    isRequired?: boolean;
    isReadOnly?: boolean;

    // v3 props
    disabled?: boolean;
    invalid?: boolean;
    required?: boolean;
    readOnly?: boolean;
  }
>;

export default function FormControl({
  isDisabled,
  disabled,
  isInvalid,
  invalid,
  isRequired,
  required,
  isReadOnly,
  readOnly,
  ...props
}: FormControlProps): JSX.Element {
  // Map v1/v2 props to v3 props for backward compatibility
  const disabledValue = disabled !== undefined ? disabled : isDisabled;
  const invalidValue = invalid !== undefined ? invalid : isInvalid;
  const requiredValue = required !== undefined ? required : isRequired;
  const readOnlyValue = readOnly !== undefined ? readOnly : isReadOnly;

  return (
    <ChakraField.Root
      disabled={disabledValue}
      invalid={invalidValue}
      required={requiredValue}
      readOnly={readOnlyValue}
      {...props}
    />
  );
}

export type FormLabelProps = ChakraFieldLabelProps & {
  isOptional?: boolean;
};

export function FormLabel({
  children,
  isOptional,
  ...props
}: PropsWithChildren<FormLabelProps>): JSX.Element {
  // Check if we're inside a Field.Root context
  let hasFieldContext = false;
  try {
    const fieldContext = useFieldContext();
    hasFieldContext = fieldContext !== undefined;
  } catch (e) {
    // No field context available
    hasFieldContext = false;
  }

  // If we have Field context, use Field.Label (Chakra v3 way)
  if (hasFieldContext) {
    return (
      <ChakraField.Label {...props}>
        {children}
        {isOptional ? <OptionalIndicator /> : null}
      </ChakraField.Label>
    );
  }

  // Otherwise, render a plain label for backwards compatibility
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label
      {...props}
      style={{
        display: "block",
        fontWeight: 500,
        marginBottom: "0.5rem",
        ...props.style,
      }}
    >
      {children}
      {isOptional ? <OptionalIndicator /> : null}
    </label>
  );
}

export type FormErrorMessageProps = Omit<
  ChakraFieldErrorTextProps,
  "children"
> & {
  // Accept ReactNode, react-hook-form's FieldError, Formik errors, or string/string[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: ReactNode | { message?: string } | string | string[] | any;
};

export function FormErrorMessage({
  children,
  ...props
}: FormErrorMessageProps): JSX.Element {
  // Handle different error types:
  // - react-hook-form's FieldError objects that have a message property
  // - string[] from Formik - join into single string
  // - Complex objects - try to extract message or stringify
  let content: ReactNode;

  if (children == null) {
    content = null;
  } else if (typeof children === "string" || typeof children === "number") {
    content = children;
  } else if (Array.isArray(children)) {
    // string[] or ReactNode[] - filter strings and join
    const strings = children.filter((c): c is string => typeof c === "string");
    content = strings.length > 0 ? strings.join(", ") : null;
  } else if (typeof children === "object" && "message" in children) {
    // FieldError with message
    content = String(children.message ?? "");
  } else {
    // ReactNode or fallback
    content = children as ReactNode;
  }

  // Check if we're inside a Field.Root context
  let hasFieldContext = false;
  try {
    const fieldContext = useFieldContext();
    hasFieldContext = fieldContext !== undefined;
  } catch (e) {
    // No field context available
    hasFieldContext = false;
  }

  // If we have Field context, use Field.ErrorText (Chakra v3 way)
  if (hasFieldContext) {
    return (
      <ChakraField.ErrorText color="error.fg" {...props}>
        {content}
      </ChakraField.ErrorText>
    );
  }

  // Otherwise, render a plain div for backwards compatibility
  return (
    <div
      {...props}
      style={{
        color: "#E53E3E", // red.500
        fontSize: "md",
        marginTop: "0.5rem",
        ...props.style,
      }}
    >
      {content}
    </div>
  );
}

export type FormHelperTextProps = PropsWithChildren<ChakraFieldHelperTextProps>;

export function FormHelperText({
  children,
  style,
  ...props
}: FormHelperTextProps): JSX.Element {
  // Check if we're inside a Field.Root context
  let hasFieldContext = false;
  try {
    const fieldContext = useFieldContext();
    hasFieldContext = fieldContext !== undefined;
  } catch (e) {
    // No field context available
    hasFieldContext = false;
  }

  // If we have Field context, use Field.HelperText (Chakra v3 way)
  if (hasFieldContext) {
    return (
      <ChakraField.HelperText style={style} {...props}>
        {children}
      </ChakraField.HelperText>
    );
  }

  // Otherwise, render a plain div for backwards compatibility
  return (
    <div
      {...props}
      style={{
        color: "#718096", // gray.600
        fontSize: "0.875rem",
        marginTop: "0.5rem",
        ...style,
      }}
    >
      {children}
    </div>
  );
}
